// ---------------------------------------------------------------------------
// PricingTable — three-tier card grid (OPUS-D20)
// ---------------------------------------------------------------------------
// Renders the Starter / Agency / Enterprise tiers as a responsive card grid.
// Primary "Choose" CTA is emerald, secondary is sky-outlined — per the brand
// rules in CLAUDE.md. The card matching the tenant's active plan displays a
// "Current plan" badge in the header.
// ---------------------------------------------------------------------------

import {
	formatIncluded,
	formatUsdMinor,
	type PricingTierView,
	type TierCode,
} from "./api";

export interface PricingTableProps {
	tiers: PricingTierView[];
	currentPlan: TierCode;
	isLoading: boolean;
	isError: boolean;
	onRetry?: () => void;
	/**
	 * Invoked when the user clicks the "Choose" CTA on a card. Defaults to a
	 * no-op so the component remains usable in snapshot tests.
	 */
	onChoose?: (tier: PricingTierView) => void;
	/** Invoked when the secondary CTA ("See details") is clicked. */
	onDetails?: (tier: PricingTierView) => void;
}

export function PricingTable({
	tiers,
	currentPlan,
	isLoading,
	isError,
	onRetry,
	onChoose,
	onDetails,
}: PricingTableProps) {
	return (
		<div className="flex flex-col gap-4">
			{isError ? (
				<RetryBanner onRetry={onRetry} />
			) : null}

			<div
				className="grid gap-6 lg:grid-cols-3"
				role="list"
				aria-label="Pricing plans"
			>
				{tiers.map((tier) => (
					<PricingCard
						key={tier.tier_code}
						tier={tier}
						isCurrent={tier.tier_code === currentPlan}
						isLoading={isLoading}
						onChoose={onChoose}
						onDetails={onDetails}
					/>
				))}
			</div>

			<p className="mt-2 text-xs text-[color:var(--color-text-faint)]">
				All plans billed in USD. Custom-quote plans include a tailored
				commitment and volume discount — pricing is confirmed during the
				sales conversation.
			</p>
		</div>
	);
}

// ---- card ----------------------------------------------------------------

interface PricingCardProps {
	tier: PricingTierView;
	isCurrent: boolean;
	isLoading: boolean;
	onChoose?: (tier: PricingTierView) => void;
	onDetails?: (tier: PricingTierView) => void;
}

function PricingCard({
	tier,
	isCurrent,
	isLoading,
	onChoose,
	onDetails,
}: PricingCardProps) {
	const isCustom = tier.kind === "custom";

	return (
		<article
			role="listitem"
			aria-label={`${tier.name} plan`}
			className="flex flex-col gap-5 rounded-[8px] border bg-[color:var(--color-surface)] p-6 shadow-[var(--shadow-brand-card)]"
			style={{
				borderColor: isCurrent
					? "var(--color-brand-emerald)"
					: "var(--color-border-strong)",
				borderWidth: isCurrent ? 2 : 1,
			}}
		>
			<header className="flex flex-col gap-2">
				<div className="flex items-center justify-between gap-2">
					<h2 className="text-xl font-semibold tracking-tight text-[color:var(--color-text)]">
						{tier.name}
					</h2>
					{isCurrent ? <CurrentPlanBadge /> : null}
				</div>
				<p className="text-sm text-[color:var(--color-text-muted)]">
					{tier.tagline}
				</p>
			</header>

			<PriceLine tier={tier} isCustom={isCustom} />

			<FeatureList highlights={tier.feature_highlights} />

			<div className="mt-auto flex flex-col gap-2">
				<button
					type="button"
					disabled={isLoading}
					onClick={() => onChoose?.(tier)}
					className="inline-flex w-full items-center justify-center rounded-[6px] bg-[color:var(--color-brand-emerald)] px-4 py-2.5 text-sm font-semibold text-white shadow-[var(--shadow-brand-subtle)] transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-brand-emerald)]/40 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{isCurrent ? "Continue" : "Choose"}
				</button>
				<button
					type="button"
					disabled={isLoading}
					onClick={() => onDetails?.(tier)}
					className="inline-flex w-full items-center justify-center rounded-[6px] border px-4 py-2.5 text-sm font-semibold text-[color:var(--color-brand-sky)] transition hover:bg-[color:var(--color-bg-alt)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-brand-sky)]/40 disabled:cursor-not-allowed disabled:opacity-60"
					style={{ borderColor: "var(--color-brand-sky)" }}
				>
					See details
				</button>
			</div>
		</article>
	);
}

// ---- price --------------------------------------------------------------

function PriceLine({
	tier,
	isCustom,
}: {
	tier: PricingTierView;
	isCustom: boolean;
}) {
	if (isCustom) {
		return (
			<div className="flex flex-col gap-1">
				<span className="text-3xl font-semibold tracking-tight text-[color:var(--color-text)]">
					Custom quote
				</span>
				<span className="text-xs text-[color:var(--color-text-muted)]">
					Pricing tailored to committed volume and term.
				</span>
			</div>
		);
	}
	return (
		<div className="flex flex-col gap-1">
			<div className="flex items-baseline gap-1.5">
				<span className="text-4xl font-semibold tracking-tight text-[color:var(--color-text)]">
					{formatUsdMinor(tier.monthly_price_usd)}
				</span>
				<span className="text-sm text-[color:var(--color-text-muted)]">
					/ month
				</span>
			</div>
			<span className="text-xs text-[color:var(--color-text-muted)]">
				Includes {formatIncluded(tier.included_conversions)} conversions.
				Overage at {formatUsdMinor(tier.overage_per_conversion_usd * 100)} /
				1,000.
			</span>
		</div>
	);
}

// ---- feature list -------------------------------------------------------

function FeatureList({ highlights }: { highlights: string[] }) {
	if (highlights.length === 0) {
		return (
			<p className="text-sm text-[color:var(--color-text-muted)]">
				Features published during the sales conversation.
			</p>
		);
	}
	return (
		<ul className="flex flex-col gap-2" aria-label="Plan features">
			{highlights.map((line, idx) => (
				// eslint-disable-next-line react/no-array-index-key -- highlights are
				// not individually mutable and have no stable id on the wire.
				<li
					key={`${line}-${idx}`}
					className="flex items-start gap-2 text-sm text-[color:var(--color-text)]"
				>
					<CheckMark />
					<span className="leading-snug">{line}</span>
				</li>
			))}
		</ul>
	);
}

function CheckMark() {
	// Inline SVG checkmark — avoids bringing in an icon library and keeps the
	// component tree tree-shakeable.
	return (
		<svg
			aria-hidden="true"
			focusable="false"
			width="16"
			height="16"
			viewBox="0 0 20 20"
			fill="none"
			className="mt-0.5 shrink-0"
		>
			<circle
				cx="10"
				cy="10"
				r="9"
				fill="var(--color-brand-emerald)"
				opacity="0.12"
			/>
			<path
				d="M5.5 10.25 L8.75 13.5 L14.5 7.5"
				stroke="var(--color-brand-emerald)"
				strokeWidth="1.75"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

// ---- current-plan badge -------------------------------------------------

function CurrentPlanBadge() {
	return (
		<span
			className="inline-flex items-center gap-1 rounded-[6px] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
			style={{
				background:
					"linear-gradient(90deg, var(--color-brand-emerald) 0%, var(--color-brand-sky) 100%)",
				color: "white",
			}}
		>
			Current plan
		</span>
	);
}

// ---- retry banner -------------------------------------------------------

function RetryBanner({ onRetry }: { onRetry?: () => void }) {
	return (
		<div
			role="alert"
			className="flex items-center justify-between gap-3 rounded-[6px] border bg-[color:var(--color-bg-alt)] px-4 py-3 text-sm text-[color:var(--color-text-muted)]"
			style={{ borderColor: "var(--color-border-strong)" }}
		>
			<span>
				We could not reach the pricing service — showing the standard plan
				sheet. Refresh when the connection is back.
			</span>
			{onRetry ? (
				<button
					type="button"
					onClick={onRetry}
					className="rounded-[6px] border px-3 py-1 text-xs font-semibold text-[color:var(--color-brand-sky)] hover:bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--color-brand-sky)]/40"
					style={{ borderColor: "var(--color-brand-sky)" }}
				>
					Retry
				</button>
			) : null}
		</div>
	);
}
